import { useCallback, useState } from 'react';
import { PROGRESSBAR_VARIANTS } from './types';
import { variants } from './variants';
import useIntegerAnimation from '../AnimatedCounter/useIntegerAnimation';

interface IProgressBarProps {
  currentProgress?: number;
  previousProgress?: number;
  variant?: PROGRESSBAR_VARIANTS;
  label?: string;
  isValueDisplayed?: boolean;
  hasBackground?: boolean;
  hasBorder?: boolean;
  withAnimation?: boolean;
  animationDuration?: number;
  onAnimationStart?: () => void;
  onAnimationEnd?: () => void;
  height?: number;
}

const MainProgressBar = ({
  currentProgress = 0,
  previousProgress = 0,
  variant = PROGRESSBAR_VARIANTS.PRIMARY,
  label,
  isValueDisplayed = false,
  hasBackground = false,
  hasBorder = false,
  withAnimation = false,
  animationDuration = 3,
  onAnimationStart,
  onAnimationEnd,
  height = 10,
  ...props
}: IProgressBarProps) => {
  const [width, setWidth] = useState<number>(0);
  const progressBarRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const { background, bar, container } = variants[variant];
  const initial = previousProgress >= 100 ? 100 : previousProgress;
  const progress = currentProgress >= 100 ? 100 : currentProgress;

  const { animatedValue } = useIntegerAnimation({
    initial,
    target: progress,
    isAnimated: withAnimation,
    duration: animationDuration * 1000,
  });

  return (
    <>
      <div className="progress-bar" {...props}>
        <div ref={progressBarRef} className="progress-bar-inner">
          <div className="bar-container-back">
            <div className="bar">{isValueDisplayed && `${animatedValue}%`}</div>
          </div>
          <div
            className="bar-container-front"
            onAnimationStart={onAnimationStart}
            onAnimationEnd={onAnimationEnd}
          >
            <div className="bar">{isValueDisplayed && `${animatedValue}%`}</div>
          </div>
        </div>
        {label && (
          <div className="progress-bar-label tw-font-bold tw-text-blue-600">
            {label}
          </div>
        )}
      </div>
      <style jsx>{`
        .progress-bar {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          width: 100%;
        }

        .progress-bar-inner {
          position: relative;
          width: 100%;
          height: ${height}px;
          line-height: ${height}px;
          overflow: hidden;
          font-family: var(--font-family-1);
          font-size: var(--font-size-1-5);
          font-weight: var(--font-weight-bold);
          border-radius: 999em;
          border: ${hasBorder ? container.border : 'none'};
        }

        .bar-container-back {
          position: absolute;
          height: 100%;
          width: 100%;
          overflow: hidden;
          border-radius: 999em;
          border: ${hasBackground ? background.border : 'none'};
        }

        .bar-container-back .bar {
          position: absolute;
          width: ${width ? `${width}px` : '100%'};
          text-align: center;
          height: 100%;
          color: ${background.color};
          background-color: ${hasBackground
            ? background.backgroundColor
            : 'transparent'};
          border: none;
        }

        .bar-container-front {
          position: absolute;
          height: 100%;
          width: ${progress}%;
          overflow: hidden;
          border-radius: 999em;
          border: bar.border;
          animation: ${withAnimation && !(initial >= 100 && progress > 100)
            ? `progress-bar ${
                animationDuration || '3'
              }s cubic-bezier(0.2, 0, 0.54, 1)`
            : 'none'};
        }

        .bar-container-front .bar {
          position: absolute;
          width: ${width ? `${width}px` : '100%'};
          text-align: center;
          height: 100%;
          color: ${bar.color};
          background-color: ${bar.backgroundColor};
          border: none;
        }

        .progress-bar-label {
          font-weight: var(--font-weight-bold);
          color: var(--clr-tertiary-1);
        }

        @keyframes progress-bar {
          0% {
            width: ${withAnimation ? initial : progress}%;
          }
          100% {
            width: ${progress}%;
          }
        }
      `}</style>
    </>
  );
};

export default MainProgressBar;
